import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import { isDevEnvironment } from "$routes/_lib/environment";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { useDemoAssignments } from "../_lib/demo-assignments";
import { formatDicomPatientName } from "../_lib/dicom-patient-name";
import { EXAM_ICONS, EXAM_LABELS } from "../_lib/epocus-exam-constants";
import { IDocumentsAccepted, UltrasoundScanner } from "../_lib/epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../_lib/epocus-palette";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { findReviewer } from "../_lib/reviewers";
import {
  ALL_VIEWERS,
  type DemoViewerId,
  useDemoViewer,
} from "../_lib/use-demo-viewer";
import { useEpocusSeedData } from "../_lib/use-epocus-seed-data";
import { linkToEpocusReviewEncounter } from "../links";

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
    gap: 20,
  },
});

const PageTitle = styled(Typography)({
  fontSize: rem(20),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const PageSubtitle = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 4,
});

const QueueCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: "14px 16px",
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  alignItems: "center",
  gap: 12,
  cursor: "pointer",
  "&:hover": {
    borderColor: EPOCUS_PRIMARY,
  },
  "&:active": {
    opacity: 0.8,
  },
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
});

const ExamIconBox = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: EPOCUS_BG,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  flexShrink: 0,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const CardInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const PatientName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const CardDetail = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const EmptyState = styled(Box)({
  textAlign: "center",
  padding: "48px 16px",
});

const EmptyTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  marginBottom: 6,
});

const EmptyBody = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (isoDate: string): string => {
  const d = new Date(isoDate);
  return d.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── Queue Item ───────────────────────────────────────────────────────────────

const AssignedChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  alignSelf: "flex-start",
  marginTop: 4,
  padding: "2px 8px",
  borderRadius: 999,
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_PRIMARY,
  fontSize: rem(11),
  fontWeight: 600,
});

const QueueItem: FC<{
  encounter: EpocusEncounter;
  exams: EpocusExam[];
  assignedReviewerName: string | null;
}> = ({ encounter, exams, assignedReviewerName }) => {
  const navigate = useNavigate();

  // Navigate into the encounter's review context. The exams themselves are
  // pulled on-demand there (they may not be on this device yet), so the card
  // stays clickable off the globally-replicated encounter alone.
  const handleClick = () => {
    navigate(linkToEpocusReviewEncounter(encounter.id));
  };

  const examTypeLabels = exams.map(
    (e) => EXAM_LABELS[e.examType] ?? e.examType,
  );
  const totalImages = exams.reduce((sum, e) => sum + e.images.length, 0);

  return (
    <QueueCard
      role="button"
      tabIndex={0}
      aria-label={`Review ${formatDicomPatientName(encounter.patientName) || "patient"}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleClick();
        } else if (e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <ExamIconBox>
        {exams.length > 0 ? (
          exams.map((e) => {
            const Icon = EXAM_ICONS[e.examType] ?? UltrasoundScanner;
            return (
              <Icon
                key={e.id}
                width={20}
                height={20}
                fill={EPOCUS_ICON_ACTIVE}
                aria-hidden="true"
              />
            );
          })
        ) : (
          <UltrasoundScanner
            width={20}
            height={20}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
          />
        )}
      </ExamIconBox>
      <CardInfo>
        <PatientName>
          {formatDicomPatientName(encounter.patientName) || "Unknown Patient"}
        </PatientName>
        <CardDetail>
          {examTypeLabels.length > 0
            ? `${examTypeLabels.join(" · ")} · `
            : null}
          {totalImages > 0 ? `${totalImages} images · ` : null}
          {formatDate(encounter.date)}
        </CardDetail>
        {encounter.accessionNumber && (
          <CardDetail style={{ fontStyle: "italic" }}>
            {encounter.accessionNumber}
          </CardDetail>
        )}
        {assignedReviewerName && (
          <AssignedChip>Assigned to {assignedReviewerName}</AssignedChip>
        )}
      </CardInfo>
      <ChevronRightIcon
        style={{ color: EPOCUS_TEXT_SECONDARY, flexShrink: 0, fontSize: 20 }}
      />
    </QueueCard>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

// Demo-only assign-to-reviewer scaffolding is gated off in production (PNE-552).
// The demo state lives in localStorage-backed hooks, so it's isolated in
// DemoReviewQueue — only mounted when the flag is on — to keep the flag-off
// path free of demo subscriptions entirely.
const EMPTY_ASSIGNMENTS: Record<string, string> = {};

export const EpocusReviewPage: FC = () => {
  const [assignToReviewerEnabled] = useFeatureFlag(
    featureFlags.epocus.assignToReviewer,
  );

  return assignToReviewerEnabled ? (
    <DemoReviewQueue />
  ) : (
    <ReviewQueue viewerId={ALL_VIEWERS} assignments={EMPTY_ASSIGNMENTS} />
  );
};

const DemoReviewQueue: FC = () => (
  <ReviewQueue viewerId={useDemoViewer()} assignments={useDemoAssignments()} />
);

const ReviewQueue: FC<{
  viewerId: DemoViewerId;
  assignments: Record<string, string>;
}> = ({ viewerId, assignments }) => {
  const { seed } = useEpocusSeedData();

  const { result: allEncounters, isFetching: encountersFetching } =
    useRxData<EpocusEncounter>(
      RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
      (collection) =>
        collection.find({
          selector: { status: { $eq: "pending_review" } },
          sort: [{ date: "asc" }],
        }),
    );

  // Demo filter — when a specific reviewer is in focus, hide encounters
  // assigned to anyone else. Unassigned encounters always stay visible.
  const encounters = useMemo(() => {
    if (viewerId === ALL_VIEWERS) return allEncounters;
    return allEncounters.filter((enc) => {
      const assigned = assignments[enc.id];
      return !assigned || assigned === viewerId;
    });
  }, [allEncounters, assignments, viewerId]);

  const encounterIds = useMemo(() => encounters.map((e) => e.id), [encounters]);
  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { encounterId: { $in: encounterIds } } })
        : collection.find({ selector: { encounterId: { $eq: "__none__" } } }),
    [encounterIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  // Group exams by encounterId
  const examsByEncounter = exams.reduce<Record<string, EpocusExam[]>>(
    (acc, exam) => {
      if (!acc[exam.encounterId]) acc[exam.encounterId] = [];
      acc[exam.encounterId].push(exam);
      return acc;
    },
    {},
  );

  if (encountersFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={32} style={{ color: EPOCUS_PRIMARY }} />
      </Box>
    );
  }

  return (
    <PageRoot>
      <Box>
        <PageTitle>Review Queue</PageTitle>
        {encounters.length > 0 && (
          <PageSubtitle>
            {encounters.length} encounter
            {encounters.length > 1 ? "s" : ""} pending your review
          </PageSubtitle>
        )}
      </Box>

      {encounters.length === 0 ? (
        <EmptyState>
          <IDocumentsAccepted
            width={48}
            height={48}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
            style={{ display: "block", margin: "0 auto 12px" }}
          />
          <EmptyTitle>No exams pending review</EmptyTitle>
          <EmptyBody>
            All caught up! New exams will appear here when they&apos;re ready.
          </EmptyBody>
          {isDevEnvironment() && (
            <Button
              variant="outlined"
              size="small"
              onClick={seed}
              style={{ marginTop: 16 }}
            >
              Seed dev data
            </Button>
          )}
        </EmptyState>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
          {encounters.map((enc) => {
            // The "Assigned to {name}" chip is only useful in the admin/all
            // view, where the queue contains other reviewers' encounters too.
            // In a single reviewer's queue, every visible item is assigned
            // (or unassigned) to them — the chip would be redundant noise.
            const assigned =
              viewerId === ALL_VIEWERS
                ? findReviewer(assignments[enc.id])
                : null;
            return (
              <QueueItem
                key={enc.id}
                encounter={enc}
                exams={examsByEncounter[enc.id] ?? []}
                assignedReviewerName={assigned?.name ?? null}
              />
            );
          })}
        </Box>
      )}
    </PageRoot>
  );
};
